import { PureComponent } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import Select from '@atlaskit/select';
import { gridSize } from '@atlaskit/theme';

import { ProfileRepositoriesLanguagesState } from 'src/redux/profile/repositories/types';
import { getProfileRepositoriesLanguagesStateSlice } from 'src/redux/profile/selectors';
import * as filterStyles from 'src/sections/repository/styles/filters';
import { selectControlStyles } from 'src/styles/select';

import { RepositoryFilters, LanguageOption, Language } from '../../../types';

import messages from './languages-filter.i18n';

type Props = ProfileRepositoriesLanguagesState & {
  intl: InjectedIntl;
  selectedLanguage: string | null;
  onFilterChange: (filters: Partial<RepositoryFilters>) => void;
};

const LONG_LANGUAGE_SELECTED_WIDTH = gridSize() * 19;
const SHORT_LANGUAGE_SELECTED_WIDTH = gridSize() * 12;

export class LanguagesFilter extends PureComponent<Props> {
  handleLanguageChange = (option: LanguageOption) => {
    this.props.onFilterChange({ language: option ? option.value : undefined });
  };

  mapFilterOption = (language: Language) => {
    return {
      label: language.name,
      value: language.slug,
    };
  };

  getValue = (selectedLanguage: string | null) => {
    const languagesOptions = this.getFilterOptions();
    if (!selectedLanguage) return null;

    return languagesOptions.find(
      (item: LanguageOption) => item.value === selectedLanguage
    );
  };

  getNoOptionsMessage = () => {
    const { intl, isError } = this.props;

    if (isError) {
      return intl.formatMessage(messages.filterError);
    }
    return intl.formatMessage(messages.filterNoOptions);
  };

  getFilterOptions = (): LanguageOption[] => {
    const { languages } = this.props;
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!languages.length) {
      return languages.map(this.mapFilterOption);
    }
    return [];
  };

  render() {
    const { intl, selectedLanguage, isLoading } = this.props;
    // Since select does not support flexible width (depending on the selected option length), a conditional width was added.
    const selectedLanguageWidth =
      selectedLanguage && selectedLanguage.length > 5
        ? LONG_LANGUAGE_SELECTED_WIDTH
        : SHORT_LANGUAGE_SELECTED_WIDTH;
    return (
      <Select
        options={this.getFilterOptions()}
        isClearable
        isLoading={isLoading}
        isDisabled={isLoading}
        placeholder={intl.formatMessage(messages.filterLanguagePlaceholder)}
        onChange={this.handleLanguageChange}
        value={this.getValue(selectedLanguage)}
        noOptionsMessage={this.getNoOptionsMessage}
        isSearchable={false}
        styles={{
          // @ts-ignore TODO: fix noImplicitAny error here
          control: (css, state) => ({
            ...css,
            ...filterStyles.subtleSelector(css, state),
            ...selectControlStyles,
            minWidth: selectedLanguage
              ? selectedLanguageWidth
              : gridSize() * 14,
            maxWidth: gridSize() * 28,
          }),
          // @ts-ignore TODO: fix noImplicitAny error here
          menu: css => ({
            ...css,
            minWidth: gridSize() * 24,
          }),
          // @ts-ignore TODO: fix noImplicitAny error here
          placeholder: css => ({
            ...css,
            ...filterStyles.nothingSelectedColor(),
          }),
        }}
      />
    );
  }
}

// @ts-ignore TODO: fix noImplicitAny error here
const mapStateToProps = (state): ProfileRepositoriesLanguagesState =>
  getProfileRepositoriesLanguagesStateSlice(state);

export default connect(mapStateToProps)(injectIntl(LanguagesFilter));
